import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  LocalStorageContent,
  PersonalizationEngineResponse
} from './data-access/data-models/personalization-engine.interface';
import { FlowRouterService } from './data-access/flow-router.service';

@Injectable({
  providedIn: 'root'
})

/**
 * This service is established in order to maintain stickeness to a user experience. A project might be metred
 * by AEM for the user to experience either the BAU flow or the Next Gen flow. With this service, we are making
 * sure that the user experiences the same flow when they revisit the experience within a set time frame.
 * AEM feature flag needs to consist of:
 * 1. A stickiness flag with options 'ON/OFF'.
 * 2. Campaign ID for metering
 * 3. A stickiness expiry in hours as a string
 */
export class FlowRouterBridgeService {
  constructor(private bridge: FlowRouterService) {}

  checkSessionActive(): boolean {
    const sessionObject: LocalStorageContent = JSON.parse(
      localStorage.getItem('flowRouterSession') || '{}'
    );
    return (
      Object.keys(sessionObject).length > 0 &&
      new Date().getTime() < sessionObject.expiresIn
    );
  }

  /**
   * Function that takes in config and returns redirect endpoint via metering or from localstorage if session exists
   * @param    {String} campaignId    Config sent from AEM
   * @param    {String} expiryInHrs    Config sent from AEM
   * @return   {Observable<String>} responseUrl
   */

  setSession(
    peResponse: PersonalizationEngineResponse,
    expiryInHrs: string
  ): LocalStorageContent {
    localStorage.clear();
    const sessionObject: LocalStorageContent = {
      redirectUrl: peResponse.contentDetails.parsys.router.endpoint,
      expiresIn: new Date().getTime() + Number(expiryInHrs) * 60 * 60 * 1000
    };
    localStorage.setItem('stickinessSession', JSON.stringify(sessionObject));
    return sessionObject;
  }

  setStickiness(campaignId: string, expiryInHrs: string): Observable<string> {
    return this.bridge.getResponse(campaignId).pipe(
      switchMap((peResponse) => {
        const sessionActive = this.checkSessionActive();
        if (!sessionActive) {
          const sessionObject = this.setSession(peResponse, expiryInHrs);
          return of(sessionObject.redirectUrl);
        }
        return of(
          JSON.parse(localStorage.getItem('flowRouterSession') || '{}')
            .redirectUrl
        );
      })
    );
  }
}
